import type {
  Counts,
  Direction,
  Event,
  LoginResponse,
  PerStageCounts,
  ScanResult,
  Stage
} from "@attendik/types/schemas";

const TOKEN_KEY = "attendik.access_token";
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

// Thrown for every non-2xx response and for network failures (status 0).
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  // Authenticated requests attach the bearer token; false for /auth/login.
  auth?: boolean;
}

class ApiClient {
  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = "GET", body, auth = true } = options;

    const headers: Record<string, string> = {};
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
    }
    if (auth) {
      const token = getToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        method,
        cache: method === "GET" ? "no-store" : "default",
        headers,
        body: body === undefined ? undefined : JSON.stringify(body)
      });
    } catch {
      throw new ApiError(0, "NETWORK", "Could not connect. Check your connection.");
    }

    // An expired/invalid token on an authenticated route ends the session.
    if (response.status === 401 && auth) {
      clearToken();
      if (window.location.pathname !== "/login") {
        window.location.assign("/login");
      }
      throw new ApiError(401, "UNAUTHORIZED", "Your session has expired.");
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const text = await response.text();
    let payload: unknown = null;
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = null;
      }
    }

    if (!response.ok) {
      const envelope = payload as { error?: string; code?: string } | null;
      throw new ApiError(
        response.status,
        envelope?.code ?? "ERROR",
        envelope?.error ?? "Request failed"
      );
    }

    return payload as T;
  }

  login(email: string, password: string): Promise<LoginResponse> {
    return this.request<LoginResponse>("/auth/login", {
      method: "POST",
      body: { email, password },
      auth: false
    });
  }

  async logout(): Promise<void> {
    await this.request<void>("/auth/logout", { method: "POST" });
  }

  getEvents(): Promise<Event[]> {
    return this.request<Event[]>("/events");
  }

  submitScan(
    eventId: string,
    badgeId: string,
    direction: Direction,
    // CR-04. Optional stage_id; required when the event uses stages.
    stageId?: string
  ): Promise<ScanResult> {
    const body: Record<string, string> = { badge_id: badgeId, direction };
    if (stageId) {
      body.stage_id = stageId;
    }
    return this.request<ScanResult>(`/events/${eventId}/scans`, {
      method: "POST",
      body
    });
  }

  getCounts(eventId: string): Promise<Counts> {
    return this.request<Counts>(`/events/${eventId}/counts`);
  }

  // CR-04. Lists stages for an event. `mine=true` returns only those the
  // authenticated operator is assigned to via stage_operators.
  getStages(eventId: string, mine = false): Promise<Stage[]> {
    const path = mine
      ? `/events/${eventId}/stages?mine=true`
      : `/events/${eventId}/stages`;
    return this.request<Stage[]>(path);
  }

  // CR-04. Per-stage counts (currently_in / capacity / my_*).
  getStageCounts(stageId: string): Promise<PerStageCounts> {
    return this.request<PerStageCounts>(`/stages/${stageId}/counts`);
  }
}

export const api = new ApiClient();
